import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { WantAddedEvent } from 'types/interfaces/followingFeed';
import { CardThumbnail } from './CardThumbnail';
import { YouHaveThisPill } from './YouHaveThisPill';
import { FeedCardShell, profileHref } from './FeedCardShell';
import { CardFan } from './CardFan';

interface WantAddedCardProps {
  event: WantAddedEvent;
}

const PREVIEW_COUNT = 10;
const EXPANDED_CAP = 50;

export const WantAddedCard: React.FC<WantAddedCardProps> = ({ event }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const cards = event.payload.cards;
  const total = cards.length;
  const ownedCount = event.viewerOwnsCount ?? 0;
  const isSingle = total === 1;
  const isExpandable = total > PREVIEW_COUNT;
  const wantListHref = `${profileHref(event.actor)}?tab=want-list`;

  const actionText =
    total === 0
      ? 'updated their want list'
      : isSingle
        ? 'wants a new card'
        : `wants ${total} new cards`;

  const hero =
    total > 0 ? (
      <CardFan cards={cards} max={PREVIEW_COUNT} showOverflowLabel />
    ) : null;

  const subline =
    isSingle && cards[0] ? (
      <div className="feed-want-subline">
        <span className="feed-want-card-name">{cards[0].cardName}</span>
        {(cards[0].setCode || cards[0].collectorNumber) && (
          <>
            <span className="feed-want-sep">·</span>
            <span className="feed-want-meta">
              {[
                cards[0].setCode?.toUpperCase(),
                cards[0].collectorNumber
                  ? `#${cards[0].collectorNumber}`
                  : null,
              ]
                .filter(Boolean)
                .join(' ')}
            </span>
          </>
        )}
        {cards[0].pointValue != null && (
          <>
            <span className="feed-want-sep">·</span>
            <Points type="small" points={cards[0].pointValue} />
          </>
        )}
      </div>
    ) : null;

  const footer = (
    <>
      {ownedCount > 0 && (
        <YouHaveThisPill
          actorUsername={event.actor.username}
          ownedCount={ownedCount}
          totalInEvent={total}
        />
      )}
      <MagicButton
        label="View want list"
        color="purple"
        onPress={() => navigate(wantListHref)}
      />
    </>
  );

  const overflowsGrid = total > EXPANDED_CAP;
  const expandLabel = isExpanded
    ? 'Hide cards ↑'
    : overflowsGrid
      ? `Show ${EXPANDED_CAP} of ${total} cards ↓`
      : `Show all ${total} cards ↓`;

  const belowFooter = isExpandable ? (
    <div className="feed-want-expandable">
      <button
        type="button"
        className="feed-want-expand-toggle"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
      >
        {expandLabel}
      </button>

      <div
        className={`feed-want-expanded ${
          isExpanded ? 'feed-want-expanded--open' : ''
        }`}
        aria-hidden={!isExpanded}
      >
        <div className="feed-want-expanded-grid">
          {cards.slice(0, EXPANDED_CAP).map((card, i) => (
            <CardThumbnail
              key={`grid-${card.cardId}-${i}`}
              card={card}
              index={i + 1000}
              className="feed-want-expanded-thumb"
            />
          ))}
        </div>
        {overflowsGrid && (
          <a href={wantListHref} className="feed-want-view-full">
            View full want list →
          </a>
        )}
      </div>
    </div>
  ) : null;

  return (
    <FeedCardShell
      actor={event.actor}
      actionText={actionText}
      createdAt={event.createdAt}
      hero={hero}
      subline={subline}
      footer={footer}
      belowFooter={belowFooter}
    />
  );
};

export default WantAddedCard;
